import React from "react";
import { toast } from "react-toastify";
import { FiCheckCircle, FiXCircle, FiEye } from "react-icons/fi";

// Status names are company-defined, so match loosely: any "fail" is red, any
// "pass" is green, everything else (In QA) is blue "ready to test".
export const notificationKind = (toStatus) => {
    const s = (toStatus || "").toLowerCase();
    if (s.includes("fail")) return "fail";
    if (s.includes("pass")) return "pass";
    return "test";
};

const KIND_UI = {
    fail: { Icon: FiXCircle, pill: "fd-status-pill--danger", label: (s) => s },
    pass: { Icon: FiCheckCircle, pill: "fd-status-pill--success", label: (s) => s },
    test: { Icon: FiEye, pill: "fd-status-pill--info", label: () => "Ready for you to test" },
};

export const kindUi = (toStatus) => {
    const kind = notificationKind(toStatus);
    const ui = KIND_UI[kind];
    return { kind, Icon: ui.Icon, pill: ui.pill, label: ui.label(toStatus || "") };
};

const sameDay = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
const clock = (d) => d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });

// "just now", "5 min ago", "3 hr ago", "Yesterday, 4:10 PM", "3 Nov, 4:10 PM"
export const timeAgo = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    const now = new Date();
    const mins = Math.floor((now - d) / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins} min ago`;
    if (sameDay(d, now)) return `${Math.floor(mins / 60)} hr ago`;
    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    if (sameDay(d, yesterday)) return `Yesterday, ${clock(d)}`;
    return `${d.toLocaleDateString([], { day: "numeric", month: "short" })}, ${clock(d)}`;
};

// "Today" | "Yesterday" | "Earlier" — history drawer section headings
export const dayGroup = (iso) => {
    const d = new Date(iso);
    const now = new Date();
    if (sameDay(d, now)) return "Today";
    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    return sameDay(d, yesterday) ? "Yesterday" : "Earlier";
};

const ToastBody = ({ item, onOpenTicket, closeToast }) => {
    const { Icon, kind } = kindUi(item.to_status);
    return (
        <div className={`sn-toast sn-toast--${kind}`}>
            <div className="sn-toast__title"><Icon size={14} /> {item.to_status} · {item.ticket_code}</div>
            {item.ticket_title && <div className="sn-toast__ticket">{item.ticket_title}</div>}
            <div className="sn-toast__msg">{item.message}</div>
            {onOpenTicket && (
                <button type="button" className="sn-toast__btn" onClick={() => { onOpenTicket(item); closeToast?.(); }}>
                    Open ticket
                </button>
            )}
        </div>
    );
};

const TOAST_TYPE = { fail: "error", pass: "success", test: "info" };

/**
 * Show newly arrived notifications. Fails stay 10 s (they need action), the
 * rest 5 s. More than three at once collapse into one summary toast.
 */
export const showNotificationToasts = (items, { onOpenTicket } = {}) => {
    if (!items?.length) return;
    if (items.length > 3) {
        const fails = items.filter(i => notificationKind(i.to_status) === "fail").length;
        const text = `${items.length} tickets in this sprint changed status${fails ? ` (${fails} failed)` : ""}. Open the bell to see them.`;
        toast[fails ? "error" : "info"](text, { autoClose: fails ? 10000 : 5000 });
        return;
    }
    items.forEach(item => {
        const kind = notificationKind(item.to_status);
        toast[TOAST_TYPE[kind]](
            ({ closeToast }) => <ToastBody item={item} onOpenTicket={onOpenTicket} closeToast={closeToast} />,
            { autoClose: kind === "fail" ? 10000 : 5000 },
        );
    });
};
